import { useQueryClient } from "@tanstack/react-query";
import { LogOut, Moon, Sun } from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import { logOut } from "../../services/AuthService";

const ROW = "flex w-full items-center justify-between gap-3 py-4 text-left";

type AppearanceChoice = "light" | "dim" | "black";

/** Light / Dim / Black, each previewed in its own page, text and line colours. */
const APPEARANCE_CHOICES: { value: AppearanceChoice; label: string; page: string; ink: string; line: string; edge: string }[] = [
  { value: "light", label: "Light", page: "#FFFFFF", ink: "#15121A", line: "#E9E6EE", edge: "#E4E1EA" },
  { value: "dim", label: "Dim", page: "#1E1E21", ink: "#F4F4F5", line: "#3A3A41", edge: "#3A3A41" },
  { value: "black", label: "Black", page: "#0B0B0D", ink: "#F4F4F5", line: "#2A2A30", edge: "#2A2A30" },
];

/**
 * Appearance and Sign out on the main Settings list. Free accounts use the public deals
 * site, which has no members sidebar or bottom tabs (where paid members find these).
 */
export default function FreeAccountRows({ t }: { t: (key: string) => string }) {
  const { theme, setTheme, darkStyle, setDarkStyle } = useTheme();
  const current: AppearanceChoice = theme === "light" ? "light" : darkStyle === "gray" ? "dim" : "black";
  const pick = (value: AppearanceChoice) => {
    if (value !== "light") setDarkStyle(value === "dim" ? "gray" : "black");
    setTheme(value === "light" ? "light" : "dark");
  };
  const queryClient = useQueryClient();

  const signOut = () => {
    queryClient.clear();
    logOut();
  };

  return (
    <>
      <div className="py-4" data-testid="settings-appearance-row">
        <div className="flex items-center gap-3">
          {theme === "dark" ? (
            <Moon className="h-4 w-4 flex-shrink-0 text-gray-800 dark:text-dark-100" />
          ) : (
            <Sun className="h-4 w-4 flex-shrink-0 text-gray-800 dark:text-dark-100" />
          )}
          <div id="settings-appearance-label" className="text-sm font-medium text-gray-900 dark:text-white">
            {t("settings.appearance")}
          </div>
        </div>
        <div role="radiogroup" aria-labelledby="settings-appearance-label" className="mt-4 grid max-w-[360px] grid-cols-3 gap-3">
          {APPEARANCE_CHOICES.map((choice) => {
            const selected = current === choice.value;
            return (
              <button
                key={choice.value}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => pick(choice.value)}
                className="group flex flex-col items-center gap-2 focus-visible:outline-none"
              >
                <span
                  className={`block w-full rounded-[18px] border-2 p-[3px] transition-colors group-focus-visible:border-[#FFD24A] ${
                    selected ? "border-[#FFD24A]" : "border-transparent"
                  }`}
                >
                  {/* A tiny screen in that theme's colours. */}
                  <span
                    className="flex aspect-[0.66] flex-col gap-1.5 rounded-[13px] border px-2 py-2.5"
                    style={{ background: choice.page, borderColor: choice.edge }}
                  >
                    <span className="block h-2 w-3/5 rounded" style={{ background: choice.ink, opacity: 0.9 }} />
                    <span className="grid grid-cols-2 gap-1">
                      <span className="block aspect-square rounded-md bg-[#F2F2F3]" />
                      <span className="block aspect-square rounded-md bg-[#F2F2F3]" />
                    </span>
                    <span className="block h-1.5 w-[90%] rounded" style={{ background: choice.line }} />
                    <span className="block h-1.5 w-[55%] rounded" style={{ background: choice.line }} />
                  </span>
                </span>
                <span className={`flex items-center gap-1.5 text-[13px] font-semibold ${selected ? "text-gray-900 dark:text-white" : "text-gray-600 dark:text-dark-200"}`}>
                  <span
                    aria-hidden="true"
                    className={`h-3.5 w-3.5 rounded-full ${selected ? "border-4 border-[#FFD24A]" : "border-[1.5px] border-gray-400 dark:border-dark-300"}`}
                  />
                  {choice.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      <button type="button" onClick={signOut} className={`${ROW} transition-colors hover:bg-gray-50 dark:hover:bg-dark-900/40`} data-testid="settings-sign-out">
        <div className="flex items-center gap-3">
          <LogOut className="h-4 w-4 flex-shrink-0 text-red-600 dark:text-red-400" />
          <div className="text-sm font-medium text-red-600 dark:text-red-400">{t("auth.signOut")}</div>
        </div>
      </button>
    </>
  );
}
